import { useEffect, useMemo, useState } from 'react'
import { RateFragment, useDailyAvailabilitiesQuery, useGetAllAvailableResourcesQuery, useEditRRResourceTypesQuery, useRatesQuery, RateType, useResourcesLookupQuery } from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { compareDates, getDayDiff, isDateBetween, isValidDate } from 'src/utils/date'
import * as R from 'ramda'
import { ResType, Res, ResourceTypeResource } from 'src/app/types/resources.types'
import { Chi, DateRange } from './edit-rr.types'
import { displayResourceType } from '../../../../../../../../utils/resource-type'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ReservedResourceF } from '../../../../types/reservation-detail.types'

export default function useEditRRQuery(
  propertyId: string, //
  reservedResource: ReservedResourceF,
  start: Date | null,
  end: Date | null,
  rateId: string | null,
  adults: number | null,
  children: Chi[],
  isTimeResource?: boolean,
  time?: { from: Date; to: Date },
  timeResourceTypeId?: string,
) {
  const localizeString = useLocalizeString()
  const { id: tenantId } = useTenant()
  const hotelDate = useHotelDate()

  const { data: ratesData, loading: ratesDataLoading } = useRatesQuery({
    variables: {
      filter: { tenantId: { eq: tenantId }, propertyId: { eq: propertyId } },
    },
  })
  const rates = useMemo(() => ratesData?.rates ?? [], [ratesData])
  const rate = useMemo(() => (rateId ? rates.find((r) => r.id === rateId) ?? null : null), [rates, rateId])

  const { data: resourceTypesData, loading: resourceTypesDataLoading } = useEditRRResourceTypesQuery({
    variables: {
      tenantId,
      propertyId,
      timeResources: isTimeResource,
    },
  })

  const resourceTypes = useMemo(() => {
    return resourceTypesData?.resourceTypes ?? []
  }, [resourceTypesData?.resourceTypes])

  const resourceTypeIds = useMemo(() => resourceTypes.map((rt) => rt.id), [JSON.stringify(resourceTypes)])

  const [dateRange, setDateRange] = useState<DateRange | null>(null)

  useEffect(() => {
    const newStart = start ?? dateRange?.start ?? null
    if (!isValidDate(newStart)) return
    const newEnd = end ?? dateRange?.end ?? null
    if (!isValidDate(newEnd)) return
    if (isTimeResource && compareDates(newStart, newEnd) > 0) return
    if (!isTimeResource && compareDates(newStart, newEnd) >= 0) return
    setDateRange({ start: newStart, end: newEnd })
  }, [start, end, isTimeResource])

  const skipQuery = useMemo(() => resourceTypeIds.length === 0 || dateRange === null, [resourceTypeIds, dateRange])

  const { data: dailyAvailabilitiesData, loading: dailyAvailabilitiesDataLoading } = useDailyAvailabilitiesQuery({
    variables: {
      tenantId,
      propertyId,
      start: dateRange?.start as Date,
      end: dateRange?.end as Date,
      resourceTypeIds,
    },
    fetchPolicy: 'cache-first',
    skip: skipQuery,
  })

  const dailyAvailabilities = useMemo(() => {
    if (!dateRange) return []

    const rrResourceType = reservedResource.currentResourceOccupancy.reservedResourceType.id
    const rrStart = reservedResource.start
    const rrEnd = reservedResource.end

    return (dailyAvailabilitiesData?.dailyAvailabilities ?? []) //
      .filter((da) => isDateBetween(da.day, dateRange.start, dateRange.end, false, isTimeResource ? false : true))
      .map((da) => {
        if (da.resourceTypeId === rrResourceType && isDateBetween(da.day, rrStart, rrEnd, false, true)) {
          return {
            ...da,
            availability: da.availability + 1,
          }
        } else {
          return da
        }
      })
  }, [
    JSON.stringify(dailyAvailabilitiesData), //
    reservedResource.currentResourceOccupancy.reservedResourceType.id,
    reservedResource.start,
    reservedResource.end,
    dateRange,
    isTimeResource,
  ])

  const resTypes: ResType[] = useMemo(() => {
    if (!dateRange) return []
    const grouped = byResourceTypeId(dailyAvailabilities)
    const keys = Object.keys(grouped)
    const daysDiff = getDayDiff(dateRange.start, dateRange.end)

    const result = keys.reduce((p: ResType[], resourceTypeId) => {
      const rt = resourceTypes.find((rtt) => rtt.id === resourceTypeId)
      if (!rt) return p
      const a = grouped[resourceTypeId]
      const display = displayResourceType(localizeString, rt)
      const overbooked = a.filter((aa) => aa.availability > 0).length < daysDiff
      const guestsCompatible = isResourceTypeGuestsCompatible(rt, adults, children)
      const rateCompatible = isResourceTypeRateCompatible(rt, rate)
      const maxAdultsCompatible = rt.maxAdults === null || rt.maxAdults === undefined || adults === null ? true : adults <= rt.maxAdults
      return [
        ...p,
        {
          resourceTypeId, //
          display,
          overbooked,
          guestsCompatible,
          rateCompatible,
          maxAdultsCompatible,
        },
      ]
    }, [])
    return result
  }, [JSON.stringify(dailyAvailabilities), resourceTypes, dateRange, adults, children, rate])

  const [lastResourceOccupancyDateRange, setLastResourceOccupancyDateRange] = useState<DateRange | null>(null)
  useEffect(() => {
    const resourceOccupanciesLenght = reservedResource.resourceOccupancies.length
    const last = [...reservedResource.resourceOccupancies].sort((a, b) => +a.start - +b.start).pop()
    if (last) {
      setLastResourceOccupancyDateRange({ start: resourceOccupanciesLenght > 1 ? hotelDate : last.start, end: last.end })
    }
  }, [reservedResource.resourceOccupancies, hotelDate])

  const dailyAvailabilityFromDate = useMemo(() => {
    if (lastResourceOccupancyDateRange) {
      if (compareDates(hotelDate, lastResourceOccupancyDateRange.start) < 0) return lastResourceOccupancyDateRange.start
      else return hotelDate
    }
    return dateRange?.start as Date
  }, [lastResourceOccupancyDateRange, dateRange, hotelDate])

  const { data: availableResourcesData, loading: availableResourceDataLoading } = useGetAllAvailableResourcesQuery({
    variables: {
      tenantId,
      propertyId,
      start: dailyAvailabilityFromDate,
      end: lastResourceOccupancyDateRange?.end ?? (dateRange?.end as Date),
      excludeReservedResourceId: reservedResource.id,
      time: time,
      resourceTypeId: timeResourceTypeId,
    },
    fetchPolicy: 'cache-first',
    skip: skipQuery,
  })

  const assignableResources: Res[] = useMemo(() => {
    return (availableResourcesData?.getAllAvailableResources ?? []) //
      .filter((ar) => resourceTypeIds.includes(ar.resourceTypeId))
      .map((ar) => ({
        resourceTypeId: ar.resourceTypeId,
        resourceId: ar.id,
        code: ar.code,
      }))
  }, [JSON.stringify(availableResourcesData), resourceTypeIds])

  const { data: resourcesQueryResult, loading: resourcesQueryLoading } = useResourcesLookupQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
      },
    },
  })

  const resources = useMemo(() => {
    return (
      resourcesQueryResult?.resources?.map<ResourceTypeResource>((r) => {
        const rt = resourceTypes.find((resourceType) => resourceType.id === r.resourceType.id)
        return {
          resource: { code: r.code, resourceId: r.id, resourceTypeId: r.resourceType.id },
          resourceType: {
            resourceTypeId: r.resourceType.id,
            display: r.resourceType.code || localizeString(r.resourceType.customName) || localizeString(r.resourceType.description),
            guestsCompatible: rt ? isResourceTypeGuestsCompatible(rt, adults, children) : false,
            maxAdultsCompatible: rt ? (rt.maxAdults === null || rt.maxAdults === undefined || adults === null ? true : adults <= rt.maxAdults) : false,
            overbooked: false,
            rateCompatible: rt ? isResourceTypeRateCompatible(rt, rate) : false,
          },
        }
      }) ?? []
    )
  }, [JSON.stringify(resourcesQueryResult?.resources), resourceTypes, adults, children, rate])

  return {
    rates,
    rate,
    resourceTypes: resTypes,
    assignableResources,
    resources,
    loading: ratesDataLoading || resourceTypesDataLoading || dailyAvailabilitiesDataLoading || availableResourceDataLoading || resourcesQueryLoading,
  }
}

function isResourceTypeGuestsCompatible(resourceType: { maxAdults?: number | null; persons: number; additionalPersons: number }, adults: number | null, children: Chi[]): boolean {
  const a = Math.max(0, adults ?? 0)
  const c = children.reduce((p: number, v) => (v.noOccupancy ? p : p + Math.max(0, v.quantity ?? 0)), 0)
  const maxAdults = resourceType.maxAdults ?? null
  if (maxAdults !== null && a > maxAdults) return false
  if (a + c > resourceType.persons + resourceType.additionalPersons) return false
  return true
}

function isResourceTypeRateCompatible(resourceType: { id: string }, rate: RateFragment | null): boolean {
  if (!rate) return false
  return (rate.resourcesTypeEnabledId ?? []).includes(resourceType.id) || rate.type === RateType.BASE
}

const byResourceTypeId = R.groupBy((da: { availability: number; day: Date; resourceTypeId: string }) => da.resourceTypeId)
